import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import useTranslation from "../hooks/useTranslation";

/**
 * Classic Payment Link 3DS challenge modal.
 * Supports Nicepay HTML form markup and JSON redirectUrlHttp.
 * Listens for `nicepay_3ds_result` postMessage from the ACS callback page.
 */
export default function ThreeDSChallengeModal({ isOpen, content, onClose, onResult }) {
  const { t } = useTranslation();
  const iframeRef = useRef(null);
  const blobUrlRef = useRef(null);
  const onResultRef = useRef(onResult);
  const [modalHeights, setModalHeights] = useState({ modal: 500, iframe: 420 });
  const [iframeSrc, setIframeSrc] = useState(null);

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  // Enable clicks on the dedicated portal root while open (root defaults to pointer-events:none).
  useEffect(() => {
    const root = document.getElementById("payment-link-modal-root");
    if (!root) return undefined;
    if (isOpen) {
      root.style.pointerEvents = "auto";
    } else {
      root.style.pointerEvents = "none";
    }
    return () => {
      root.style.pointerEvents = "none";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const updateHeights = () => {
      const modal = Math.round(window.innerHeight * 0.8);
      setModalHeights({ modal, iframe: Math.max(320, modal - 100) });
    };
    updateHeights();
    window.addEventListener("resize", updateHeights);
    return () => window.removeEventListener("resize", updateHeights);
  }, [isOpen]);

  // Build iframe src: HTML → blob URL (preserves <base>/scripts like dashboard), URL → direct
  useEffect(() => {
    if (!isOpen || !content) {
      setIframeSrc(null);
      return undefined;
    }

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    const trimmed = typeof content === "string" ? content.trim() : "";
    if (!trimmed) {
      setIframeSrc(null);
      return undefined;
    }

    if (trimmed.startsWith("<")) {
      const blob = new Blob([content], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setIframeSrc(url);
    } else {
      setIframeSrc(trimmed);
    }

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [isOpen, content]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== "nicepay_3ds_result") return;
      onResultRef.current?.(data);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isOpen]);

  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-4"
      style={{ position: "fixed", inset: 0, zIndex: 2147483647, pointerEvents: "auto" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tds-modal-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col"
        style={{ height: `${modalHeights.modal}px` }}
      >
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between shrink-0">
          <h3 id="tds-modal-title" className="text-lg font-semibold text-gray-900">
            {t("paymentInstructionCard.threeDsTitle")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label={t("common.close")}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="bg-gray-100 flex flex-col shrink-0" style={{ height: `${modalHeights.iframe}px` }}>
          {iframeSrc ? (
            <iframe
              ref={iframeRef}
              title={t("paymentInstructionCard.threeDsIframeTitle")}
              name="payment-link-tds-iframe"
              src={iframeSrc}
              className="w-full border-0"
              style={{ height: `${modalHeights.iframe}px` }}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              {t("paymentInstructionCard.threeDsLoading")}
            </div>
          )}
        </div>
        <p className="px-4 py-2 text-sm text-gray-500 text-center border-t border-gray-100">
          {t("paymentInstructionCard.threeDsFooter")}
        </p>
      </div>
    </div>
  );

  return createPortal(modal, document.getElementById("payment-link-modal-root") || document.body);
}
